import * as React from 'react';

export const allowedHtmlTags = ['b', 'strong', 'i', 'em', 'u', 'br'];

function hasOnlyAllowedNodes(parent: Node): boolean {
    for (let index = 0; index < parent.childNodes.length; index++) {
        const child = parent.childNodes[index];
        if (child.nodeType === Node.TEXT_NODE) {
            continue;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) {
            return false;
        }
        const element = child as Element;
        if (
            allowedHtmlTags.indexOf(element.tagName.toLowerCase()) === -1 ||
            element.attributes.length > 0 ||
            !hasOnlyAllowedNodes(element)
        ) {
            return false;
        }
    }
    return true;
}

// Parses the text the way a browser would, and returns the body only when
// every node in it is allowed. Metadata elements such as scripts and styles
// are moved into the head by the parser, so the head has to be empty as well.
function parseAllowedHtml(text: string): HTMLElement | null {
    const parsed = new DOMParser().parseFromString(text, 'text/html');
    if (parsed.head.childNodes.length > 0 || !hasOnlyAllowedNodes(parsed.body)) {
        return null;
    }
    return parsed.body;
}

export function hasOnlyAllowedHtml(text: string): boolean {
    return !text || text.indexOf('<') === -1 || parseAllowedHtml(text) !== null;
}

function toReactNodes(parent: Node): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];
    for (let index = 0; index < parent.childNodes.length; index++) {
        const child = parent.childNodes[index];
        if (child.nodeType === Node.TEXT_NODE) {
            nodes.push(child.textContent);
            continue;
        }
        const name = (child as Element).tagName.toLowerCase();
        nodes.push(
            name === 'br' ? (
                <br key={index} />
            ) : (
                React.createElement(name, { key: index }, ...toReactNodes(child))
            )
        );
    }
    return nodes;
}

export function renderAllowedHtml(text: React.ReactNode): React.ReactNode {
    if (typeof text !== 'string' || text.indexOf('<') === -1) {
        return text;
    }

    const body = parseAllowedHtml(text);
    if (body === null || body.children.length === 0) {
        return text;
    }

    return <>{toReactNodes(body)}</>;
}
